import { Check } from 'lucide-react'
import { cn } from '@/internationalization/cn'

export interface StepperStep {
  id: string
  label: string
  description?: string
}

export interface StepperProps {
  steps: StepperStep[]
  /** Index of the step in progress. Everything before it renders complete. */
  activeIndex: number
  className?: string
}

/**
 * Linear progress through onboarding. Horizontal on desktop, vertical below sm
 * — a four-step horizontal stepper truncates every label on a phone.
 */
export function Stepper({ steps, activeIndex, className }: StepperProps) {
  return (
    <ol className={cn('flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-2', className)}>
      {steps.map((step, index) => {
        const isDone = index < activeIndex
        const isActive = index === activeIndex
        return (
          <li key={step.id} className="flex flex-1 items-start gap-3 sm:flex-col sm:gap-2">
            <div className="flex items-center gap-3 sm:w-full">
              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  isDone && 'bg-success text-white',
                  isActive && 'bg-brand text-brand-fg',
                  !isDone && !isActive && 'border border-border bg-surface text-fg-subtle',
                )}
              >
                {isDone ? <Check aria-hidden className="h-3.5 w-3.5" strokeWidth={3} /> : index + 1}
              </span>
              <span
                aria-hidden
                className={cn(
                  'hidden h-px flex-1 sm:block',
                  isDone ? 'bg-success' : 'bg-border',
                  index === steps.length - 1 && 'sm:hidden',
                )}
              />
            </div>
            <div className="min-w-0">
              <p className={cn('text-base font-medium', isActive ? 'text-fg' : 'text-fg-muted')}>
                {step.label}
              </p>
              {step.description && <p className="mt-0.5 text-sm text-fg-subtle">{step.description}</p>}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
